import { useCallback } from 'react';
import type { ReactNode } from 'react';

import { track } from '@/lib/analytics';
import { cn } from '@/utils/cn';

// Utility to remove all hover: classes from a class string
const removeHoverClasses = (classStr: string): string =>
  classStr
    .split(' ')
    .filter((cls) => !cls.startsWith('hover:'))
    .join(' ');

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonColor = 'primary' | 'secondary' | 'soft' | 'ghost' | 'neutral' | 'danger';

const getButtonClassName = (size: ButtonSize, color: ButtonColor): string => {
  const base =
    'relative rounded-md font-semibold shadow-sm transition-all flex items-center justify-center cursor-pointer';
  const sizes = {
    xs: 'px-1.5 py-1 text-xs',
    sm: 'px-2 py-1 text-sm',
    md: 'px-2.5 py-1.5 text-sm',
    lg: 'px-3 py-2 text-sm',
    xl: 'px-3.5 py-3 text-sm font-semibold',
  };
  const colors = {
    primary: 'bg-amber-800 text-white hover:bg-amber-700',
    secondary: 'border border-amber-900 text-amber-900 hover:border-amber-700 hover:text-amber-700',
    neutral: 'bg-neutral-900 text-neutral-100 hover:bg-neutral-600',
    soft: 'bg-amber-50 text-amber-600 hover:bg-amber-100',
    ghost: 'shadow-none text-neutral-800 hover:text-neutral-700',
    danger: 'bg-red-600 text-white hover:bg-red-500',
  };

  return `${base} ${sizes[size]} ${colors[color]}`;
};

type Props = {
  className?: string;
  children: ReactNode;
  size?: ButtonSize;
  color?: ButtonColor;
  trackEvent?: string;
  trackProps?: Record<string, string>;
  isLoading?: boolean;
  noHover?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const Button = ({
  className = '',
  children,
  size = 'md',
  color = 'primary',
  disabled = false,
  trackEvent,
  trackProps,
  isLoading = false,
  noHover = false,
  onClick,
  ...rest
}: Props) => {
  const buttonClass = useCallback(() => {
    const baseClass = getButtonClassName(size, color);
    if (noHover) {
      return removeHoverClasses(baseClass);
    }
    return baseClass;
  }, [size, color, noHover]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      }

      if (trackEvent) {
        track(trackEvent, trackProps);
      }
    },
    [onClick, trackEvent, trackProps],
  );

  return (
    <button
      className={cn(
        buttonClass(),
        disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : '',
        noHover ? removeHoverClasses(className || '') : className,
      )}
      onClick={handleClick}
      disabled={disabled}
      {...rest}
    >
      {isLoading ? (
        <div className="absolute flex items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-50 border-t-amber-800" />
        </div>
      ) : null}
      <span className={cn('flex-1 text-center', isLoading ? 'opacity-0' : '')}>{children}</span>
    </button>
  );
};

export default Button;
